import React from 'react';
import { Modal } from '../basic';
import { Tooltip } from '../ui';
import { formatLocalDate } from '../../utils/dateUtils';

interface ProcessedDataItem {
  rowNumber: number;
  status: 'success' | 'skipped' | 'error';
  data: any;
  error?: string;
}

interface DataReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  type: 'roster' | 'schedule';
  processedData: ProcessedDataItem[];
  isValidationOnly?: boolean; // true if showing validation results only (no database operations)
}

const DataReviewModal: React.FC<DataReviewModalProps> = ({
  isOpen,
  onClose,
  title,
  type,
  processedData,
  isValidationOnly = false,
}) => {
  const getStatusIcon = (status: 'success' | 'skipped' | 'error') => {
    switch (status) {
      case 'success':
        return (
          <div className="w-5 h-5 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-3 h-3 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        );
      case 'skipped':
        return (
          <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center">
            <svg className="w-3 h-3 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
        );
      case 'error':
        return (
          <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-3 h-3 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        );
    }
  };

  const getStatusText = (status: 'success' | 'skipped' | 'error') => {
    if (isValidationOnly) {
      // When validation-only, show validation status instead of database operation status
      switch (status) {
        case 'success':
          return 'Valid data';
        case 'skipped':
          return 'Valid data'; // skipped doesn't apply in validation-only
        case 'error':
          return 'Issue found';
      }
    } else {
      // Normal database operation status
      switch (status) {
        case 'success':
          return 'Added';
        case 'skipped':
          return 'Already exists';
        case 'error':
          return 'Issue found';
      }
    }
  };

  const getStatusColor = (status: 'success' | 'skipped' | 'error') => {
    switch (status) {
      case 'success':
        return 'text-green-600';
      case 'skipped':
        return 'text-blue-600';
      case 'error':
        return 'text-red-600';
    }
  };

  const renderRosterRow = (item: ProcessedDataItem) => {
    const data = item.data;
    return (
      <tr key={item.rowNumber} className="hover:bg-gray-50">
        <td className="px-4 py-3 text-sm text-gray-500">{item.rowNumber}</td>
        <td className="px-4 py-3">
          <div className="flex items-center space-x-3">
            <Tooltip
              content={
                <ul className="list-disc list-inside">
                  {item.error?.split(';').map((error, index) => (
                    <>
                      <li key={index}>{error}</li>
                    </>
                  ))}
                </ul>
              }
              disabled={!item.error}
            >
              {getStatusIcon(item.status)}
            </Tooltip>
            <span className={`text-sm font-medium ${getStatusColor(item.status)}`}>{getStatusText(item.status)}</span>
          </div>
        </td>
        <td className="px-4 py-3 text-sm text-gray-900">{data.firstName || '-'}</td>
        <td className="px-4 py-3 text-sm text-gray-900">{data.lastName || '-'}</td>
        <td className="px-4 py-3 text-sm text-gray-900">{data.no}</td>
        <td className="px-4 py-3 text-sm text-gray-900">
          {Array.isArray(data.position) ? data.position.join(', ') : data.position || '-'}
        </td>
        <td className="px-4 py-3 text-sm text-gray-900">{data.graduation || '-'}</td>
      </tr>
    );
  };

  const renderScheduleRow = (item: ProcessedDataItem) => {
    const data = item.data;
    return (
      <tr key={item.rowNumber} className="hover:bg-gray-50">
        <td className="px-4 py-3 text-sm text-gray-500">{item.rowNumber}</td>
        <td className="px-4 py-3">
          <div className="flex items-center space-x-3">
            <Tooltip
              content={
                <ul className="list-disc list-inside">
                  {item.error?.split(';').map((error, index) => (
                    <>
                      <li key={index}>{error}</li>
                    </>
                  ))}
                </ul>
              }
              disabled={!item.error}
            >
              {getStatusIcon(item.status)}
            </Tooltip>
            <span className={`text-sm font-medium ${getStatusColor(item.status)}`}>{getStatusText(item.status)}</span>
          </div>
        </td>
        <td className="px-4 py-3 text-sm text-gray-900">{data.opponent || data.name}</td>
        <td className="px-4 py-3 text-sm text-gray-900">{data.date ? formatLocalDate(data.date) : '-'}</td>
        <td className="px-4 py-3 text-sm text-gray-900">{data.time || '-'}</td>
        <td className="px-4 py-3 text-sm text-gray-900">{data.type || '-'}</td>
        <td className="px-4 py-3 text-sm text-gray-900">{data.location || '-'}</td>
      </tr>
    );
  };

  const statusCounts = processedData.reduce(
    (acc, item) => {
      // If validation only, combine success/skipped as "valid" rows
      if (isValidationOnly) {
        if (item.status === 'success' || item.status === 'skipped') {
          acc.success++; // Count both success and skipped as "valid rows"
        } else if (item.status === 'error') {
          acc.error++;
        }
        // Don't use skipped count in validation mode
      } else {
        // Normal database operation results
        acc[item.status]++;
      }
      return acc;
    },
    { success: 0, skipped: 0, error: 0 },
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} maxWidth="6xl">
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">{title} - Data Review</h2>
            <p className="text-sm text-gray-600 mt-1">
              Here's what happened with each row in your file. Hover over the status icons to see more details.
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Summary Stats */}
        <div className={`grid gap-4 mb-6 ${isValidationOnly ? 'grid-cols-2' : 'grid-cols-3'}`}>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-green-800">{isValidationOnly ? 'Valid Rows' : 'Added'}</p>
                <p className="text-2xl font-bold text-green-600">{statusCounts.success}</p>
              </div>
            </div>
          </div>

          {!isValidationOnly && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-blue-800">Already on {type === 'roster' ? 'Team' : 'Games'}</p>
                  <p className="text-2xl font-bold text-blue-600">{statusCounts.skipped}</p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <div>
                <p className="text-sm font-medium text-red-800">Issues Found</p>
                <p className="text-2xl font-bold text-red-600">{statusCounts.error}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div className="max-h-96 overflow-y-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Row
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  {type === 'roster' ? (
                    <>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        First Name
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Last Name
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Player Number
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Position
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Graduation Year
                      </th>
                    </>
                  ) : (
                    <>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Opponent
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Time
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Location
                      </th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {processedData.map((item) => (type === 'roster' ? renderRosterRow(item) : renderScheduleRow(item)))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-md text-sm font-medium hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DataReviewModal;
